import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import mqtt, { MqttClient } from 'mqtt';
import { Terminal } from '@xterm/xterm';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { useAuth } from '@/contexts/AuthContext';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  TokenType,
  type Token,
  type TextDeltaData,
  type ThinkingDeltaData,
  type ThinkingDoneData,
  type ToolStartData,
  type ToolDoneData,
  type ResultData,
  type ErrorData,
  type TurnMessage,
  type ContentBlock,
} from '@/types/sdkToken';
import FileBrowserPanel from './FileBrowserPanel';
import { getChatHistory, type ChatSnapshot } from '@/services/chatHistoryService';
import '@xterm/xterm/css/xterm.css';

// Fixed PTY size — matches mqtt-agent's pty.StartWithSize.
const PTY_COLS = 120;
const PTY_ROWS = 40;

interface AgentStatus {
  online: boolean;
  agent_id: string;
  name: string;
  ts: number;
}

interface ChatMsg {
  id: number;
  role: 'user' | 'assistant';
  content: string;
}

// Common Claude Code prompt responses (terminal mode only)
const QUICK_ACTIONS = [
  { label: 'Yes', value: 'y', color: 'bg-green-600 hover:bg-green-700', key: 'y' },
  { label: 'No', value: 'n', color: 'bg-red-600 hover:bg-red-700', key: 'n' },
  { label: 'Retry', value: 'r', color: 'bg-yellow-600 hover:bg-yellow-700', key: 'r' },
  { label: 'Skip', value: 's', color: 'bg-gray-600 hover:bg-gray-700', key: 's' },
  { label: 'Esc', value: '\x1b', color: 'bg-gray-600 hover:bg-gray-700', key: 'Escape' },
] as const;

const NAV_ACTIONS = [
  { label: '\u2191', value: '\x1b[A', title: 'Up' },
  { label: '\u2193', value: '\x1b[B', title: 'Down' },
  { label: '\u21B5', value: '\r', title: 'Enter / Confirm' },
  { label: 'Tab', value: '\t', title: 'Tab / Next option' },
] as const;

const MAX_BUFFER_SIZE = 2 * 1024 * 1024;
const INPUT_STORAGE_KEY = 'mqtt-chat-input';

const stripAnsi = (s: string): string =>
  s.replace(/\x1b\[[0-9;]*[a-zA-Z]/g, '')
   .replace(/\x1b\][^\x07]*\x07/g, '')
   .replace(/\x1b[()][AB012]/g, '')
   .replace(/\x1b\[[\?]?[0-9;]*[hlJKm]/g, '');

const STATUS_PATTERNS: { pattern: RegExp; extract: (m: RegExpMatchArray) => string }[] = [
  { pattern: /Clauding[\u2026.]+/,          extract: (m) => m[0] },
  { pattern: /Tip:\s*(.+)/,                 extract: (m) => `Tip: ${m[1].trim()}` },
  { pattern: /([⠋⠙⠹⠸⠼⠴⠦⠧⠇⠏✓✗●◐◑◒◓⣾⣽⣻⢿⡿⣟⣯⣷])\s*(.+)/, extract: (m) => m[0] },
];

// ---------------------------------------------------------------------------
// Parse xterm.js terminal buffer into chat messages.
// Only splits on prompt lines (❯ / >) to separate user vs assistant.
// No color/decorative/box-drawing filters — everything passes through.
// ---------------------------------------------------------------------------
function parseTerminalBuffer(term: Terminal): ChatMsg[] {
  const buf = term.buffer.active;
  const totalLines = buf.baseY + buf.cursorY + 1;
  const lines: string[] = [];

  for (let i = 0; i < totalLines && i < buf.length; i++) {
    const line = buf.getLine(i);
    if (line) lines.push(line.translateToString(true).trimEnd());
  }

  const messages: ChatMsg[] = [];
  let id = 0;
  let accum: string[] = [];
  let role: ChatMsg['role'] = 'assistant';

  const flush = () => {
    const text = accum.join('\n').trim();
    if (text) messages.push({ id: id++, role, content: text });
    accum = [];
  };

  for (const line of lines) {
    const promptMatch = line.match(/^[❯>]\s*(.*)/);
    if (promptMatch) {
      flush();
      const input = promptMatch[1].trim();
      if (input) messages.push({ id: id++, role: 'user', content: input });
      role = 'assistant';
      continue;
    }
    accum.push(line);
  }
  flush();
  return messages;
}

// ---------------------------------------------------------------------------
// Convert ChatSnapshot[] from Redis history into TurnMessage[] for structured view.
// Groups consecutive same-role snapshots into single turns.
// ---------------------------------------------------------------------------
function snapshotsToTurns(
  snapshots: ChatSnapshot[],
  turnIdRef: React.MutableRefObject<number>,
): TurnMessage[] {
  const turns: TurnMessage[] = [];
  for (const snap of snapshots) {
    const role = snap.role === 'user' ? 'user' : 'assistant';

    if (snap.role === 'tool') {
      // Tool usage → assistant turn with tool block (content has file path / command info)
      const toolBlock = { kind: 'tool' as const, toolId: '', toolName: snap.tool ?? '', input: snap.content, done: true };
      const last = turns[turns.length - 1];
      if (last && last.role === 'assistant') {
        last.blocks.push(toolBlock);
      } else {
        turns.push({
          id: turnIdRef.current++,
          role: 'assistant',
          blocks: [toolBlock],
        });
      }
      continue;
    }

    if (snap.role === 'result') {
      const last = turns[turns.length - 1];
      if (last && last.role === 'assistant') {
        last.blocks.push({ kind: 'result', sessionId: '', costUsd: 0, duration: snap.content });
      }
      continue;
    }

    if (snap.role === 'error') {
      const last = turns[turns.length - 1];
      if (last && last.role === 'assistant') {
        last.blocks.push({ kind: 'error', message: snap.content });
      } else {
        turns.push({
          id: turnIdRef.current++,
          role: 'assistant',
          blocks: [{ kind: 'error', message: snap.content }],
        });
      }
      continue;
    }

    // User or assistant text
    const last = turns[turns.length - 1];
    if (last && last.role === role) {
      // Merge text into existing turn
      const lastBlock = last.blocks[last.blocks.length - 1];
      if (lastBlock?.kind === 'text') {
        lastBlock.content += snap.content;
      } else {
        last.blocks.push({ kind: 'text', content: snap.content });
      }
    } else {
      turns.push({
        id: turnIdRef.current++,
        role,
        blocks: [{ kind: 'text', content: snap.content }],
      });
    }
  }
  return turns;
}

// ---------------------------------------------------------------------------
// Render message content as clickable lines for chat bubbles.
// Each non-empty line in assistant messages becomes clickable → appends to input.
// ---------------------------------------------------------------------------
function renderChatContent(content: string, onLineClick?: (text: string) => void): React.ReactNode {
  const lines = content.split('\n');
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (onLineClick && trimmed) {
      return (
        <div
          key={i}
          onClick={() => onLineClick(trimmed)}
          className="cursor-pointer hover:bg-gray-700/50 rounded px-1 -mx-1 transition-colors"
          title="Click to copy to input"
        >
          {line || '\u00A0'}
        </div>
      );
    }
    return <div key={i}>{line || '\u00A0'}</div>;
  });
}

// ---------------------------------------------------------------------------
// Structured block renderer for SDK token view
// ---------------------------------------------------------------------------
const TOOL_ICONS: Record<string, string> = {
  Read: '\uD83D\uDCC4',   // 📄
  Edit: '\u270F\uFE0F',   // ✏️
  Write: '\uD83D\uDCDD',  // 📝
  Bash: '\uD83D\uDCBB',   // 💻
  Grep: '\uD83D\uDD0D',   // 🔍
  Glob: '\uD83D\uDCC1',   // 📁
};

function StructuredBlock({ block, isUser }: { block: ContentBlock; isUser: boolean }) {
  const [expanded, setExpanded] = useState(false);

  if (block.kind === 'text') {
    return (
      <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        isUser
          ? 'bg-blue-600 text-white rounded-br-md'
          : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 shadow-sm rounded-bl-md'
      }`}>
        {isUser ? (
          <div className="whitespace-pre-wrap break-words">{block.content}</div>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none break-words
            prose-table:border-collapse prose-table:w-full prose-table:text-xs
            prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:px-3 prose-th:py-1.5 prose-th:bg-gray-100 dark:prose-th:bg-gray-700 prose-th:text-left prose-th:font-semibold
            prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:px-3 prose-td:py-1.5
            prose-code:bg-gray-100 dark:prose-code:bg-gray-700 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none
            prose-pre:bg-gray-900 prose-pre:text-green-300 prose-pre:text-xs
            prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5
          ">
            <Markdown remarkPlugins={[remarkGfm]}>{block.content}</Markdown>
          </div>
        )}
      </div>
    );
  }

  if (block.kind === 'thinking') {
    return (
      <div className="rounded-lg border border-purple-200 dark:border-purple-800/50 bg-purple-50 dark:bg-purple-900/10 px-3 py-2 text-xs">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 w-full text-left"
        >
          <span className="text-purple-500">
            {!block.done
              ? <span className="inline-block w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
              : '\uD83E\uDDE0'}
          </span>
          <span className="font-medium text-purple-700 dark:text-purple-300">Thinking</span>
          {!block.done && <span className="text-purple-400 text-[10px]">reasoning...</span>}
          <span className="ml-auto text-gray-400">{expanded ? '\u25B2' : '\u25BC'}</span>
        </button>
        {expanded && (
          <pre className="mt-2 text-[10px] text-purple-700 dark:text-purple-300 font-mono whitespace-pre-wrap break-words max-h-64 overflow-y-auto bg-purple-100 dark:bg-purple-900/20 rounded p-2">
            {block.content}
          </pre>
        )}
      </div>
    );
  }

  if (block.kind === 'tool') {
    const icon = TOOL_ICONS[block.toolName] || '\u2699\uFE0F';
    const p = block.parsedInput;

    // Build a concise preview from parsed input fields
    let inputPreview = '';
    if (p) {
      if (typeof p.command === 'string') inputPreview = p.command;
      else if (typeof p.file_path === 'string') inputPreview = p.file_path;
      else if (typeof p.pattern === 'string') inputPreview = p.pattern;
      else if (typeof p.query === 'string') inputPreview = p.query;
    }
    if (!inputPreview) inputPreview = block.input.slice(0, 120);

    return (
      <div className={`rounded-lg border px-3 py-2 text-xs ${
        block.done
          ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
          : 'border-yellow-500/30 bg-yellow-50 dark:bg-yellow-900/10'
      }`}>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 w-full text-left"
        >
          <span>{icon}</span>
          <span className="font-medium text-gray-700 dark:text-gray-300">{block.toolName}</span>
          {!block.done && <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />}
          {block.done && <span className="text-green-600 dark:text-green-400 text-[10px]">done</span>}
          <span className="ml-auto text-gray-400">{expanded ? '\u25B2' : '\u25BC'}</span>
        </button>
        {!expanded && inputPreview && (
          <p className="mt-1 text-gray-500 dark:text-gray-400 font-mono truncate">{inputPreview}</p>
        )}
        {expanded && p && (
          <div className="mt-2 space-y-1">
            {Object.entries(p).map(([key, val]) => (
              <div key={key} className="flex gap-2">
                <span className="text-gray-500 dark:text-gray-500 font-medium shrink-0">{key}:</span>
                <span className="text-gray-700 dark:text-gray-300 font-mono break-all whitespace-pre-wrap">
                  {typeof val === 'string' ? val : JSON.stringify(val, null, 2)}
                </span>
              </div>
            ))}
          </div>
        )}
        {expanded && !p && block.input && (
          <pre className="mt-2 text-[10px] text-gray-600 dark:text-gray-400 font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto bg-gray-100 dark:bg-gray-900 rounded p-2">
            {block.input}
          </pre>
        )}
      </div>
    );
  }

  if (block.kind === 'result') {
    return (
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/10 px-3 py-2 text-xs">
        <div className="flex items-center gap-3 text-emerald-700 dark:text-emerald-400">
          <span className="font-medium">Turn complete</span>
          {block.costUsd > 0 && <span>${block.costUsd.toFixed(4)}</span>}
          {block.duration && <span>{block.duration}</span>}
        </div>
      </div>
    );
  }

  if (block.kind === 'error') {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
        Error: {block.message}
      </div>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
type ViewMode = 'terminal' | 'chat' | 'sdk' | 'files';
const VALID_VIEWS: ViewMode[] = ['terminal', 'chat', 'sdk', 'files'];
const DEFAULT_VIEW: ViewMode = 'sdk';
// Backward compat: accept old 'structured' param
const normalizeView = (v: string | null): ViewMode | null => {
  if (v === 'structured') return 'sdk';
  if (v && VALID_VIEWS.includes(v as ViewMode)) return v as ViewMode;
  return null;
};

const VIEW_OPTIONS: { mode: ViewMode; label: string; icon: React.ReactNode; activeColor: string }[] = [
  {
    mode: 'terminal',
    label: 'Terminal',
    activeColor: 'bg-gray-500 text-white',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    mode: 'chat',
    label: 'Chat',
    activeColor: 'bg-blue-600 text-white',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
  {
    mode: 'sdk',
    label: 'SDK',
    activeColor: 'bg-emerald-600 text-white',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
      </svg>
    ),
  },
  {
    mode: 'files',
    label: 'Files',
    activeColor: 'bg-amber-600 text-white',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    ),
  },
];

const MqttChatViewer: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState<MqttClient | null>(null);
  const [connected, setConnected] = useState(false);
  const [agents, setAgents] = useState<Map<string, AgentStatus>>(new Map());
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState(() => {
    const agentParam = searchParams.get('agent');
    if (agentParam) return localStorage.getItem(`${INPUT_STORAGE_KEY}-${agentParam}`) || '';
    return '';
  });
  const [agentStatus, setAgentStatus] = useState<string | null>(null);
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);

  // View mode from ?view= query param, default to SDK
  const viewMode: ViewMode = normalizeView(searchParams.get('view')) ?? DEFAULT_VIEW;
  // Track which non-files view to return to from files tab
  const [chatViewMode, setChatViewMode] = useState<Exclude<ViewMode, 'files'>>(() => {
    const v = normalizeView(searchParams.get('view'));
    return v && v !== 'files' ? v : 'sdk';
  });

  const setViewMode = useCallback((mode: ViewMode) => {
    if (mode !== 'files') setChatViewMode(mode as Exclude<ViewMode, 'files'>);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('view', mode);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [turnMessages, setTurnMessages] = useState<TurnMessage[]>([]);
  const [activeTool, setActiveTool] = useState<{ name: string; input: string } | null>(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const parseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const termRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const clientRef = useRef<MqttClient | null>(null);
  const selectedAgentRef = useRef<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const outputBuffers = useRef<Map<string, Uint8Array[]>>(new Map());
  const bufferSizes = useRef<Map<string, number>>(new Map());
  const turnIdRef = useRef(0);
  const structuredEndRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const sdkScrollRef = useRef<HTMLDivElement>(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  useEffect(() => { clientRef.current = client; }, [client]);
  useEffect(() => { selectedAgentRef.current = selectedAgent; }, [selectedAgent]);

  // Persist input value to localStorage per agent
  useEffect(() => {
    if (selectedAgent) {
      localStorage.setItem(`${INPUT_STORAGE_KEY}-${selectedAgent}`, inputValue);
    }
  }, [inputValue, selectedAgent]);

  // Restore agent selection from ?agent= query param
  useEffect(() => {
    const agentParam = searchParams.get('agent');
    if (agentParam && !selectedAgent && agents.has(agentParam)) {
      const agent = agents.get(agentParam);
      if (agent?.online) {
        setSelectedAgent(agentParam);
      }
    }
  }, [agents, selectedAgent, searchParams]);

  const detectStatus = useCallback((raw: Uint8Array) => {
    const text = stripAnsi(new TextDecoder().decode(raw));
    for (const { pattern, extract } of STATUS_PATTERNS) {
      const m = text.match(pattern);
      if (m) {
        const status = extract(m).slice(0, 120);
        setAgentStatus(status);
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        statusTimerRef.current = setTimeout(() => setAgentStatus(null), 30_000);
        return;
      }
    }
  }, []);

  // Debounced parse of terminal buffer → chat messages
  const scheduleParse = useCallback(() => {
    if (parseTimerRef.current) clearTimeout(parseTimerRef.current);
    parseTimerRef.current = setTimeout(() => {
      if (terminalRef.current) {
        setChatMessages(parseTerminalBuffer(terminalRef.current));
      }
    }, 400);
  }, []);

  const publishInput = useCallback((data: string) => {
    if (clientRef.current && selectedAgentRef.current) {
      clientRef.current.publish(`flux/${selectedAgentRef.current}/in`, data, { qos: 0 });
    }
  }, []);

  // Handle clicking a line in an assistant bubble → append to input
  const handleLineClick = useCallback((text: string) => {
    setInputValue(prev => prev ? `${prev} ${text}` : text);
    inputRef.current?.focus();
  }, []);

  // Process a token from the /tokens MQTT topic into structured turn messages.
  const processToken = useCallback((tok: Token) => {
    const d = tok.data;

    switch (tok.type) {
      case TokenType.TextDelta: {
        const text = (d as unknown as TextDeltaData)?.text ?? '';
        if (!text) return;
        setTurnMessages(prev => {
          const msgs = [...prev];
          let last = msgs[msgs.length - 1];
          if (!last || last.role !== 'assistant') {
            last = { id: turnIdRef.current++, role: 'assistant', blocks: [] };
            msgs.push(last);
          }
          const blocks = [...last.blocks];
          const lastBlock = blocks[blocks.length - 1];
          if (lastBlock?.kind === 'text') {
            blocks[blocks.length - 1] = { ...lastBlock, content: lastBlock.content + text };
          } else {
            blocks.push({ kind: 'text', content: text });
          }
          msgs[msgs.length - 1] = { ...last, blocks };
          return msgs;
        });
        break;
      }

      case TokenType.ToolStart: {
        const ts = d as unknown as ToolStartData;
        if (!ts) return;
        setActiveTool({ name: ts.tool_name, input: '' });
        setTurnMessages(prev => {
          const msgs = [...prev];
          let last = msgs[msgs.length - 1];
          if (!last || last.role !== 'assistant') {
            last = { id: turnIdRef.current++, role: 'assistant', blocks: [] };
            msgs.push(last);
          }
          const blocks = [...last.blocks];
          blocks.push({ kind: 'tool', toolId: ts.tool_id, toolName: ts.tool_name, input: '', done: false });
          msgs[msgs.length - 1] = { ...last, blocks };
          return msgs;
        });
        break;
      }

      case TokenType.ToolInputDelta: {
        const partial = (d as { partial_json?: string })?.partial_json ?? '';
        setActiveTool(prev => prev ? { ...prev, input: prev.input + partial } : prev);
        break;
      }

      case TokenType.ToolDone: {
        const td = d as unknown as ToolDoneData;
        setActiveTool(null);
        if (!td) return;
        setTurnMessages(prev => {
          const msgs = [...prev];
          const last = msgs[msgs.length - 1];
          if (!last) return prev;
          const blocks = [...last.blocks];
          // Find the matching tool block (last unclosed one with matching toolId)
          for (let i = blocks.length - 1; i >= 0; i--) {
            const b = blocks[i];
            if (b.kind === 'tool' && b.toolId === td.tool_id && !b.done) {
              blocks[i] = { ...b, input: td.full_input, parsedInput: td.parsed_input, done: true };
              break;
            }
          }
          msgs[msgs.length - 1] = { ...last, blocks };
          return msgs;
        });
        break;
      }

      case TokenType.Result: {
        const rd = d as unknown as ResultData;
        if (!rd) return;
        setTurnMessages(prev => {
          const msgs = [...prev];
          let last = msgs[msgs.length - 1];
          if (!last || last.role !== 'assistant') {
            last = { id: turnIdRef.current++, role: 'assistant', blocks: [] };
            msgs.push(last);
          }
          const blocks = [...last.blocks];
          blocks.push({ kind: 'result', sessionId: rd.session_id, costUsd: rd.cost_usd ?? 0, duration: rd.duration ?? '' });
          msgs[msgs.length - 1] = { ...last, blocks };
          return msgs;
        });
        break;
      }

      case TokenType.Error: {
        const ed = d as unknown as ErrorData;
        if (!ed) return;
        setTurnMessages(prev => {
          const msgs = [...prev];
          let last = msgs[msgs.length - 1];
          if (!last || last.role !== 'assistant') {
            last = { id: turnIdRef.current++, role: 'assistant', blocks: [] };
            msgs.push(last);
          }
          const blocks = [...last.blocks];
          blocks.push({ kind: 'error', message: ed.err });
          msgs[msgs.length - 1] = { ...last, blocks };
          return msgs;
        });
        break;
      }

      case TokenType.ThinkingDelta: {
        const text = (d as unknown as ThinkingDeltaData)?.text ?? '';
        if (!text) return;
        setTurnMessages(prev => {
          const msgs = [...prev];
          let last = msgs[msgs.length - 1];
          if (!last || last.role !== 'assistant') {
            last = { id: turnIdRef.current++, role: 'assistant', blocks: [] };
            msgs.push(last);
          }
          const blocks = [...last.blocks];
          const lastBlock = blocks[blocks.length - 1];
          if (lastBlock?.kind === 'thinking' && !lastBlock.done) {
            blocks[blocks.length - 1] = { ...lastBlock, content: lastBlock.content + text };
          } else {
            blocks.push({ kind: 'thinking', content: text, done: false });
          }
          msgs[msgs.length - 1] = { ...last, blocks };
          return msgs;
        });
        break;
      }

      case TokenType.ThinkingDone: {
        const td = d as unknown as ThinkingDoneData;
        setTurnMessages(prev => {
          const msgs = [...prev];
          const last = msgs[msgs.length - 1];
          if (!last) return prev;
          const blocks = [...last.blocks];
          // Mark the last thinking block as done
          for (let i = blocks.length - 1; i >= 0; i--) {
            const b = blocks[i];
            if (b.kind === 'thinking' && !b.done) {
              blocks[i] = { ...b, done: true, signature: td?.signature };
              break;
            }
          }
          msgs[msgs.length - 1] = { ...last, blocks };
          return msgs;
        });
        break;
      }

      // TextDone, TurnComplete, SessionStart — no rendering action needed
      default:
        break;
    }
  }, []);

  // Connect to MQTT broker via WebSocket
  useEffect(() => {
    const wsProtocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const brokerUrl = `${wsProtocol}//${location.host}/mqtt`;
    const clientId = `viewer-${user?.username || 'anon'}-${Date.now()}`;

    const mqttClient = mqtt.connect(brokerUrl, {
      clientId,
      clean: true,
      reconnectPeriod: 3000,
      keepalive: 60,
    });

    mqttClient.on('connect', () => {
      console.log('[MqttChat] MQTT connected, clientId:', clientId);
      setConnected(true);
      mqttClient.subscribe('flux/+/status', { qos: 1 });
      mqttClient.subscribe('flux/+/out', { qos: 0 });
      mqttClient.subscribe('flux/+/tokens', { qos: 0 });
    });

    mqttClient.on('close', () => setConnected(false));
    mqttClient.on('error', (err) => console.error('MQTT error:', err));

    mqttClient.on('message', (topic, payload) => {
      const parts = topic.split('/');
      if (parts.length !== 3 || parts[0] !== 'flux') return;
      const agentId = parts[1];
      const channel = parts[2];

      if (channel === 'status') {
        try {
          const status: AgentStatus = JSON.parse(payload.toString());
          setAgents((prev) => {
            const next = new Map(prev);
            next.set(agentId, status);
            return next;
          });
        } catch {
          // ignore malformed status
        }
      } else if (channel === 'tokens') {
        if (agentId === selectedAgentRef.current) {
          try {
            const tok: Token = JSON.parse(payload.toString());
            processToken(tok);
          } catch {
            // ignore malformed token
          }
        }
      } else if (channel === 'out') {
        const data = new Uint8Array(payload);

        if (!outputBuffers.current.has(agentId)) {
          outputBuffers.current.set(agentId, []);
          bufferSizes.current.set(agentId, 0);
        }
        const chunks = outputBuffers.current.get(agentId)!;
        const currentSize = bufferSizes.current.get(agentId)! + data.length;

        if (currentSize > MAX_BUFFER_SIZE) {
          let dropped = 0;
          while (chunks.length > 0 && dropped < data.length) {
            dropped += chunks.shift()!.length;
          }
          bufferSizes.current.set(agentId, currentSize - dropped);
        } else {
          bufferSizes.current.set(agentId, currentSize);
        }
        chunks.push(data.slice());

        if (agentId === selectedAgentRef.current) {
          detectStatus(data);
          if (terminalRef.current) {
            terminalRef.current.write(data);
          }
          scheduleParse();
        }
      }
    });

    setClient(mqttClient);
    return () => { mqttClient.end(true); };
  }, [user?.username]);

  // Initialize xterm.js terminal (always created for buffer processing)
  useEffect(() => {
    if (!termRef.current || !selectedAgent) return;

    const containerWidth = termRef.current.clientWidth - 16;
    const isMobile = containerWidth < 600;
    const idealFontSize = Math.floor(containerWidth / (PTY_COLS * 0.6));
    const minFont = isMobile ? 10 : 4;
    const fontSize = Math.max(minFont, Math.min(idealFontSize, 16));

    const term = new Terminal({
      cursorBlink: true,
      cols: PTY_COLS,
      rows: PTY_ROWS,
      fontSize,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1a1b26',
        foreground: '#a9b1d6',
        cursor: '#c0caf5',
        selectionBackground: '#33467c',
        black: '#32344a',
        red: '#f7768e',
        green: '#9ece6a',
        yellow: '#e0af68',
        blue: '#7aa2f7',
        magenta: '#ad8ee6',
        cyan: '#449dab',
        white: '#787c99',
        brightBlack: '#444b6a',
        brightRed: '#ff7a93',
        brightGreen: '#b9f27c',
        brightYellow: '#ff9e64',
        brightBlue: '#7da6ff',
        brightMagenta: '#bb9af7',
        brightCyan: '#0db9d7',
        brightWhite: '#acb0d0',
      },
      convertEol: false,
      scrollback: 5000,
      disableStdin: true,
    });
    const webLinksAddon = new WebLinksAddon();
    term.loadAddon(webLinksAddon);
    term.open(termRef.current);
    terminalRef.current = term;

    const buffered = outputBuffers.current.get(selectedAgent);
    if (buffered && buffered.length > 0) {
      for (const chunk of buffered) {
        term.write(chunk);
      }
    }

    // Parse initial buffer for chat view
    setTimeout(() => {
      if (terminalRef.current) {
        setChatMessages(parseTerminalBuffer(terminalRef.current));
      }
    }, 200);

    const handleResize = () => {
      if (!termRef.current || !terminalRef.current) return;
      const w = termRef.current.clientWidth - 16;
      const mobile = w < 600;
      const newSize = Math.max(mobile ? 10 : 4, Math.min(Math.floor(w / (PTY_COLS * 0.6)), 16));
      if (newSize !== terminalRef.current.options.fontSize) {
        terminalRef.current.options.fontSize = newSize;
        terminalRef.current.resize(PTY_COLS, PTY_ROWS);
      }
    };
    window.addEventListener('resize', handleResize);
    setTimeout(() => inputRef.current?.focus(), 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      term.dispose();
      terminalRef.current = null;
    };
  }, [selectedAgent]);

  useEffect(() => {
    if (viewMode === 'chat' && chatScrollRef.current) {
      const el = chatScrollRef.current;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        setHasNewMessages(false);
      } else if (chatMessages.length > 0) {
        setHasNewMessages(true);
      }
    }
  }, [chatMessages, viewMode]);

  useEffect(() => {
    if (viewMode === 'sdk' && sdkScrollRef.current) {
      const el = sdkScrollRef.current;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
        structuredEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        setHasNewMessages(false);
      } else if (turnMessages.length > 0) {
        setHasNewMessages(true);
      }
    }
  }, [turnMessages, viewMode]);

  const handleScrollEvent = useCallback(() => {
    const el = viewMode === 'chat' ? chatScrollRef.current : sdkScrollRef.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      setHasNewMessages(false);
    }
  }, [viewMode]);

  const scrollToBottom = useCallback(() => {
    if (viewMode === 'chat') chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    else if (viewMode === 'sdk') structuredEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setHasNewMessages(false);
  }, [viewMode]);

  const handleViewToggle = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    setViewDropdownOpen(false);
    setHasNewMessages(false);
    if (mode === 'chat' && terminalRef.current) {
      setChatMessages(parseTerminalBuffer(terminalRef.current));
    }
    if (mode === 'terminal' && terminalRef.current) {
      terminalRef.current.refresh(0, PTY_ROWS - 1);
    }
  }, [setViewMode]);

  const handleSelectAgent = useCallback((agentId: string) => {
    setSelectedAgent(agentId);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('agent', agentId);
      return next;
    }, { replace: true });
    setChatMessages([]);
    setTurnMessages([]);
    setActiveTool(null);
    setHistoryCount(0);
    setHasNewMessages(false);
    turnIdRef.current = 0;
    setInputValue(localStorage.getItem(`${INPUT_STORAGE_KEY}-${agentId}`) || '');

    // Load chat history from Redis
    setLoadingHistory(true);
    getChatHistory(agentId, 100).then((snapshots) => {
      if (snapshots.length > 0) {
        const turns = snapshotsToTurns(snapshots, turnIdRef);
        setTurnMessages(turns);
        setHistoryCount(turns.length);
      }
    }).catch(() => {
      // History unavailable — continue with live stream only
    }).finally(() => setLoadingHistory(false));
  }, [setSearchParams]);

  const handleDisconnect = useCallback(() => {
    setSelectedAgent(null);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('agent');
      return next;
    }, { replace: true });
    setInputValue('');
    setAgentStatus(null);
    setChatMessages([]);
    setTurnMessages([]);
    setActiveTool(null);
    setHistoryCount(0);
    setViewDropdownOpen(false);
  }, [setSearchParams]);

  const handleSendMessage = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    publishInput(trimmed + '\r');
    // Add user message to structured view
    setTurnMessages(prev => [
      ...prev,
      { id: turnIdRef.current++, role: 'user', blocks: [{ kind: 'text', content: trimmed }] },
    ]);
    setInputValue('');
    // On mobile, blur to dismiss keyboard; on desktop, keep focus
    if (window.innerWidth < 640) {
      inputRef.current?.blur();
    } else {
      inputRef.current?.focus();
    }
  }, [inputValue, publishInput]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const handleQuickAction = useCallback((value: string) => {
    publishInput(value);
    if (value.length === 1 && value !== '\t') {
      setTimeout(() => publishInput('\r'), 30);
    }
    inputRef.current?.focus();
  }, [publishInput]);

  const handleNavAction = useCallback((value: string) => {
    publishInput(value);
    inputRef.current?.focus();
  }, [publishInput]);

  const sortedAgents = Array.from(agents.values()).sort((a, b) => {
    if (a.online !== b.online) return a.online ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Please log in to use Agent Chat Room
      </div>
    );
  }

  // Agent list view
  if (!selectedAgent) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Agent Chat Room
          </h1>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
            {connected ? 'Connected' : 'Disconnected'}
          </div>
        </div>

        {sortedAgents.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-lg font-medium">No active agents</p>
            <p className="mt-1">
              Run <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">mqtt-agent</code> on a machine to connect
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sortedAgents.map((agent) => (
              <button
                key={agent.agent_id}
                onClick={() => agent.online && handleSelectAgent(agent.agent_id)}
                disabled={!agent.online}
                className={`p-4 bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700 text-left ${
                  !agent.online ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    agent.online ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`} />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {agent.name}
                    </p>
                    <p className="text-sm text-gray-500 truncate">{agent.agent_id}</p>
                  </div>
                  <span className="text-xs text-gray-400 ml-auto">
                    {agent.online ? 'online' : 'offline'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Session view
  const agentInfo = agents.get(selectedAgent);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] sm:h-[calc(100vh-8rem)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-gray-800 text-white rounded-t-lg flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={handleDisconnect}
            className="p-2 sm:p-1 hover:bg-gray-700 rounded"
            title="Back to agents"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="font-medium text-sm sm:text-base truncate">{agentInfo?.name || selectedAgent}</span>
        </div>

        <div className="flex items-center gap-1 sm:gap-1.5">
          {/* Desktop: inline icon toggle group */}
          <div className="hidden sm:flex items-center bg-gray-700 rounded-lg p-0.5 gap-0.5">
            {VIEW_OPTIONS.filter(o => o.mode !== 'files').map(opt => (
              <button
                key={opt.mode}
                onClick={() => handleViewToggle(opt.mode)}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === opt.mode ? opt.activeColor : 'text-gray-400 hover:text-gray-200'
                }`}
                title={opt.label}
              >
                {opt.icon}
              </button>
            ))}
          </div>

          {/* Mobile: dropdown view selector */}
          <div className="relative sm:hidden">
            <button
              onClick={() => setViewDropdownOpen(prev => !prev)}
              className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-700 rounded-lg text-gray-200 text-xs"
            >
              {VIEW_OPTIONS.find(o => o.mode === viewMode)?.icon}
              <span>{VIEW_OPTIONS.find(o => o.mode === viewMode)?.label}</span>
              <svg className={`w-3 h-3 transition-transform ${viewDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {viewDropdownOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setViewDropdownOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-gray-800 border border-gray-600 rounded-lg shadow-xl py-1 min-w-[140px]">
                  {VIEW_OPTIONS.filter(o => o.mode !== 'files').map(opt => (
                    <button
                      key={opt.mode}
                      onClick={() => handleViewToggle(opt.mode)}
                      className={`flex items-center gap-2 w-full px-3 py-2 text-xs transition-colors ${
                        viewMode === opt.mode
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
                      }`}
                    >
                      {opt.icon}
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-400">
            <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="hidden sm:inline">{connected ? 'connected' : 'disconnected'}</span>
          </div>
        </div>
      </div>

      {/* Terminal view (always mounted for buffer processing, hidden when not active) */}
      <div
        ref={termRef}
        className={viewMode === 'terminal' ? 'flex-1 min-h-0 overflow-auto' : 'h-0 overflow-hidden'}
        style={{ backgroundColor: '#1a1b26' }}
      />

      {/* Files full-viewport view */}
      {viewMode === 'files' && client && (
        <div className="flex-1 min-h-0 bg-gray-900">
          <FileBrowserPanel
            client={client}
            agentId={selectedAgent}
            open={true}
            onClose={() => handleViewToggle(chatViewMode)}
            inline
          />
        </div>
      )}

          {/* Chat view (PTY-parsed bubbles) */}
          {viewMode === 'chat' && (
            <div ref={chatScrollRef} onScroll={handleScrollEvent} className="flex-1 min-h-0 overflow-y-auto bg-gray-50 dark:bg-gray-950 px-3 sm:px-4 py-4 space-y-3 relative">
              {chatMessages.length === 0 && (
                <div className="text-center text-gray-400 dark:text-gray-500 py-12 text-sm">
                  Waiting for messages...
                </div>
              )}

              {chatMessages.map((msg) => {
                const isUser = msg.role === 'user';
                return (
                  <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} gap-2`}>
                    {!isUser && (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1 shadow-sm">
                        AI
                      </div>
                    )}
                    <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      isUser
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 shadow-sm rounded-bl-md'
                    }`}>
                      <div className="whitespace-pre-wrap break-words font-mono text-xs">
                        {renderChatContent(msg.content, !isUser ? handleLineClick : undefined)}
                      </div>
                    </div>
                    {isUser && (
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1 shadow-sm">
                        {user?.username?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* AI status indicator */}
              {agentStatus && (
                <div className="flex items-center gap-2.5 px-3 py-2 mx-2 mb-1 rounded-xl bg-gradient-to-r from-purple-500/10 to-indigo-500/10 dark:from-purple-500/5 dark:to-indigo-500/5 border border-purple-200/30 dark:border-purple-800/30">
                  <div className="flex gap-1 flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce [animation-delay:0ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce [animation-delay:300ms]" />
                  </div>
                  <span className="text-xs text-purple-600 dark:text-purple-300 truncate font-medium">{agentStatus}</span>
                </div>
              )}

              {hasNewMessages && (
                <button
                  onClick={scrollToBottom}
                  className="sticky bottom-2 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-full shadow-lg transition-colors flex items-center gap-1.5"
                >
                  New messages
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>
              )}
              <div ref={chatEndRef} />
            </div>
          )}

          {/* SDK structured view */}
          {viewMode === 'sdk' && (
            <div ref={sdkScrollRef} onScroll={handleScrollEvent} className="flex-1 min-h-0 overflow-y-auto bg-gray-50 dark:bg-gray-950 px-3 sm:px-4 py-4 space-y-4 relative">
              {loadingHistory && (
                <div className="text-center text-gray-400 dark:text-gray-500 py-4 text-xs">
                  Loading history...
                </div>
              )}

              {turnMessages.length === 0 && !activeTool && !loadingHistory && (
                <div className="text-center text-gray-400 dark:text-gray-500 py-12 text-sm">
                  Waiting for SDK tokens... (agent must use <code className="bg-gray-200 dark:bg-gray-800 px-1.5 py-0.5 rounded">--sdk</code> mode)
                </div>
              )}

              {turnMessages.map((turn, idx) => {
                const isUser = turn.role === 'user';
                const showDivider = historyCount > 0 && idx === historyCount;
                return (
                  <React.Fragment key={turn.id}>
                    {showDivider && (
                      <div className="flex items-center gap-3 py-1">
                        <div className="flex-1 border-t border-dashed border-gray-300 dark:border-gray-700" />
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider whitespace-nowrap">History above &middot; Live below</span>
                        <div className="flex-1 border-t border-dashed border-gray-300 dark:border-gray-700" />
                      </div>
                    )}
                    {/* turn bubble */}
                    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} gap-2`}>
                      {!isUser && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1 shadow-sm">
                          AI
                        </div>
                      )}
                      <div className={`max-w-[90%] sm:max-w-[80%] space-y-2 ${isUser ? 'items-end' : 'items-start'}`}>
                        {turn.blocks.map((block, bi) => (
                          <StructuredBlock key={bi} block={block} isUser={isUser} />
                        ))}
                      </div>
                      {isUser && (
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1 shadow-sm">
                          {user?.username?.[0]?.toUpperCase() || 'U'}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}

              {/* Active tool progress indicator */}
              {activeTool && (
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1 shadow-sm">
                    AI
                  </div>
                  <div className="rounded-lg border border-yellow-500/30 bg-yellow-50 dark:bg-yellow-900/10 px-3 py-2 max-w-[80%]">
                    <div className="flex items-center gap-2 text-xs font-medium text-yellow-700 dark:text-yellow-400">
                      <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                      Running: {activeTool.name}
                    </div>
                    {activeTool.input && (
                      <pre className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 font-mono max-h-16 overflow-hidden whitespace-pre-wrap break-all">
                        {activeTool.input.slice(0, 200)}{activeTool.input.length > 200 ? '...' : ''}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {hasNewMessages && (
                <button
                  onClick={scrollToBottom}
                  className="sticky bottom-2 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-full shadow-lg transition-colors flex items-center gap-1.5"
                >
                  New messages
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>
              )}
              <div ref={structuredEndRef} />
            </div>
          )}

          {/* Agent status bar (terminal mode only) */}
          {viewMode === 'terminal' && agentStatus && (
            <div className="flex items-center gap-2 px-3 py-1.5 border-t border-gray-700 flex-shrink-0"
                 style={{ backgroundColor: '#2a2e3d' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse flex-shrink-0" />
              <span className="text-xs text-gray-300 truncate">{agentStatus}</span>
              <button
                onClick={() => setAgentStatus(null)}
                className="ml-auto text-gray-500 hover:text-gray-300 text-xs flex-shrink-0"
                title="Dismiss"
              >
                &times;
              </button>
            </div>
          )}

          {/* Quick actions bar (terminal mode only) */}
          {viewMode === 'terminal' && (
            <div className="flex items-center gap-1.5 sm:gap-1 px-2 sm:px-3 py-2 bg-gray-800 border-t border-gray-700 flex-shrink-0 flex-wrap">
              <span className="text-xs text-gray-500 mr-0.5 hidden sm:inline">Reply:</span>
              {QUICK_ACTIONS.map((action) => (
                <button
                  key={action.label}
                  onClick={() => handleQuickAction(action.value)}
                  className={`px-3 py-2 sm:px-2.5 sm:py-1 text-xs font-medium text-white rounded ${action.color} transition-colors`}
                  title={`Send "${action.label}" (${action.key})`}
                >
                  {action.label}
                </button>
              ))}

              <span className="w-px h-5 bg-gray-600 mx-1 sm:mx-2" />

              <span className="text-xs text-gray-500 mr-0.5 hidden sm:inline">Nav:</span>
              {NAV_ACTIONS.map((action) => (
                <button
                  key={action.title}
                  onClick={() => handleNavAction(action.value)}
                  className="px-3 py-2 sm:px-2.5 sm:py-1 text-xs font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                  title={action.title}
                >
                  {action.label}
                </button>
              ))}

              <span className="w-px h-5 bg-gray-600 mx-1 sm:mx-2" />

              <button
                onClick={() => publishInput('\x03')}
                className="px-3 py-2 sm:px-2.5 sm:py-1 text-xs font-medium text-red-300 bg-gray-700 hover:bg-red-900 rounded transition-colors"
                title="Send Ctrl+C (interrupt)"
              >
                Ctrl+C
              </button>
            </div>
          )}

      {/* Bottom tabs: Files | Chats */}
      <div className="flex bg-gray-800 border-t border-gray-700 flex-shrink-0">
        <button
          onClick={() => handleViewToggle('files')}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium transition-colors ${
            viewMode === 'files'
              ? 'bg-amber-600/20 text-amber-400 border-t-2 border-amber-500'
              : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          Files
        </button>
        <button
          onClick={() => viewMode === 'files' && handleViewToggle(chatViewMode)}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium transition-colors ${
            viewMode !== 'files'
              ? 'bg-blue-600/20 text-blue-400 border-t-2 border-blue-500'
              : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Chats
        </button>
      </div>

      {/* Message input bar (chats only) */}
      {viewMode !== 'files' && (
        <div className="flex items-center gap-2 px-2 sm:px-3 py-2 bg-gray-900 rounded-b-lg flex-shrink-0">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Type a message..."
            className="flex-1 min-w-0 px-3 py-2.5 sm:py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoComplete="off"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim()}
            className="px-4 py-2.5 sm:py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
};

export default MqttChatViewer;
